import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import styles from "./styles.module.scss";
import { useGoogleMaps } from "./hooks/useGoogleMaps";
import { getLocations } from "api/Locations";

/**
 * Landing screen for the Mulch Calculator. Two entry paths:
 *  1. Enter an address (autocomplete pulls from Google Places for Phase 1;
 *     existing-location lookup wires in once the Locations directory ships).
 *  2. Recent site maps — placeholder until Phase 1.5 backend exposes it.
 */

const MulchCalculatorLanding = () => {
  const { loaded, error } = useGoogleMaps();
  const navigate = useNavigate();
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<any>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [recentLocations, setRecentLocations] = useState<any[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Mulch Calculator";
  }, []);

  // Fetch recent locations — the top of the Locations directory sorted by
  // updatedAt, so reps landing on the calculator see their most recent work
  // without going through the Locations directory tab. Limits to 8; anything
  // longer belongs in the full directory.
  useEffect(() => {
    let cancelled = false;
    setLoadingRecent(true);
    getLocations({
      query: {
        recordsPer: 8,
        pageNum: 1,
        sortBy: "updatedAt",
        sortDir: "desc",
      },
    }).then((res: any) => {
      if (cancelled) return;
      const items = Array.isArray(res?.items) ? res.items : [];
      setRecentLocations(items);
      setLoadingRecent(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!loaded || !addressInputRef.current || autocompleteRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(
      addressInputRef.current,
      { types: ["geocode"], componentRestrictions: { country: "us" } },
    );
    autocompleteRef.current = ac;
  }, [loaded]);

  const handleLocate = () => {
    const value = addressInputRef.current?.value?.trim();
    if (!value || !loaded) return;
    setStatus("Looking up address…");
    new window.google.maps.Geocoder().geocode(
      { address: value },
      (results: any, statusCode: string) => {
        if (statusCode !== "OK" || !results?.[0]) {
          setStatus("Location not found. Try a different address.");
          return;
        }
        const loc = results[0].geometry.location;
        const lat = loc.lat();
        const lng = loc.lng();
        if (lat < 24.4 || lat > 50 || lng < -129.2 || lng > -61) {
          setStatus("Only Continental US addresses are supported.");
          return;
        }
        const formatted = results[0].formatted_address;
        // Ad-hoc mode: no location record yet. Editor accepts these
        // via query params and defers the location record creation
        // until first Save.
        const q = new URLSearchParams({
          address: formatted,
          lat: String(lat),
          lng: String(lng),
        });
        navigate(`/mulch-calculator/new/edit?${q.toString()}`);
      },
    );
  };

  return (
    <div className={styles.landing}>
      <h1 className={styles.landingTitle}>Mulch Calculator</h1>
      <p className={styles.landingSub}>
        Draw mulch areas on the satellite map to calculate yardage and see
        cost, retail, and margin for each service.
      </p>

      {error && (
        <p style={{ color: "#c00", fontSize: 13 }}>
          Google Maps failed to load: {error}
        </p>
      )}

      <div className={styles.addressBar}>
        <input
          ref={addressInputRef}
          type="text"
          placeholder="Enter a property address to start a new map…"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleLocate();
            }
          }}
        />
        <button
          className={styles.btnPrimary}
          onClick={handleLocate}
          disabled={!loaded}
        >
          Locate site
        </button>
      </div>

      {status && (
        <p style={{ fontSize: 12, color: "#5D5E62", marginTop: -20 }}>
          {status}
        </p>
      )}

      <h2 className={styles.recentTitle}>Recent site maps</h2>
      {loadingRecent ? (
        <div className={styles.recentEmpty}>Loading recent sites…</div>
      ) : recentLocations.length === 0 ? (
        <div className={styles.recentEmpty}>
          No recent sites yet. Enter an address above to draw your first map.
        </div>
      ) : (
        <div className={styles.recentList}>
          {recentLocations.map((loc: any) => {
            const addr = loc.address || {};
            const addressLine = [
              addr.line1,
              addr.city,
              addr.state,
            ]
              .filter(Boolean)
              .join(", ");
            return (
              <button
                key={loc.id}
                className={styles.recentRow}
                onClick={() => navigate(`/locations/${loc.id}`)}
              >
                <div className={styles.recentRowMain}>
                  <strong>{loc.name || "(unnamed)"}</strong>
                  {addressLine && (
                    <span className={styles.recentRowAddress}>{addressLine}</span>
                  )}
                </div>
                <span className={styles.recentRowDate}>
                  {loc.updatedAt
                    ? moment(loc.updatedAt).fromNow()
                    : "—"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MulchCalculatorLanding;
